import { useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import type { Attachment } from '@/signalr';
import { isVideoAttachment } from '@ngsos/portal-core/incidents';
import { trackAvailableMediaHeight } from './mediaViewerHeight';

export type MediaViewerDialogHandle = {
  show: (mediaItems: readonly Attachment[], index?: number) => void;
};

export type MediaViewerDialogProps = {
  ref?: Ref<MediaViewerDialogHandle>;
};

async function downloadMediaFile(url: string): Promise<void> {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.setProperty('display', 'none');
  document.body.appendChild(a);
  a.download = url.replace(/^.*[\\/]/, '');
  a.href = objectUrl;
  a.target = '_blank';
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export function MediaViewerDialog({ ref }: MediaViewerDialogProps) {
  const { t } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);

  const [items, setItems] = useState<readonly Attachment[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const current = items.length > 0 ? items[currentIndex] : undefined;

  useImperativeHandle(ref, () => ({
    show: (mediaItems: readonly Attachment[], index = 0) => {
      setItems(mediaItems);
      setCurrentIndex(Math.min(Math.max(index, 0), Math.max(0, mediaItems.length - 1)));
      modalRef.current?.show();
    },
  }));

  const navigatePrev = () => {
    setCurrentIndex((index) => (index > 0 ? index - 1 : index));
  };

  const navigateNext = () => {
    setCurrentIndex((index) => (index < items.length - 1 ? index + 1 : index));
  };

  const downloadMedia = async () => {
    if (current !== undefined) {
      await downloadMediaFile(current.url);
    }
  };

  return (
    <NgModal
      ref={modalRef}
      width="extraLarge"
      centered
      bodyTemplate={
        <div className="media-viewer-dialog" ref={trackAvailableMediaHeight}>
          {current !== undefined &&
            (isVideoAttachment(current) ? (
              <video
                key={currentIndex}
                src={current.url}
                className="media-viewer-dialog__media"
                controls
                autoPlay
              ></video>
            ) : (
              <img key={currentIndex} src={current.url} alt="Attachment" className="media-viewer-dialog__media" />
            ))}
        </div>
      }
      footerTemplate={
        <div className="d-flex w-100 justify-content-between align-items-center">
          {items.length > 1 ? (
            <div className="d-flex gap-2">
              <NgButton outline icon="fal fa-chevron-left" disabled={currentIndex <= 0} onClick={navigatePrev} />
              <NgButton
                outline
                icon="fal fa-chevron-right"
                disabled={currentIndex >= items.length - 1}
                onClick={navigateNext}
              />
            </div>
          ) : (
            <span></span>
          )}
          <NgButton outline icon="fal fa-download" onClick={downloadMedia}>
            {t('Button_Download')}
          </NgButton>
        </div>
      }
    />
  );
}
